import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { User, Mail, AtSign, AlertTriangle, Pencil, Loader2 } from 'lucide-react';
import { logout, updateProfile } from '../../../slices/authSlice';
import DeleteAccountModal from '../../../components/DeleteAccountModal/DeleteAccountModal';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import FinalConfirmationModal from '../../../components/FinalConfirmationModal/FinalConfirmationModal';
import useDeleteAccount from '../../../hooks/profile/useDeleteUser';

const ProfileInfo = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [isConfirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const [isFinalModalVisible, setFinalModalVisible] = useState(false);
  const { deleteUser } = useDeleteAccount();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', username: '' });

  const startEditing = () => {
    setForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      username: user.username || '',
    });
    setFormError('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setFormError('');
  };

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    const trimmedUsername = form.username.trim();

    if (trimmedUsername.length < 3) {
      setFormError(t('invalidUsername', 'Username must be at least 3 characters.'));
      return;
    }

    setFormError('');
    setIsSaving(true);
    try {
      await dispatch(
        updateProfile({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          username: trimmedUsername,
        })
      ).unwrap();
      setIsEditing(false);
    } catch (err) {
      setFormError(
        typeof err === 'string'
          ? err
          : err?.message || t('update_profile_error', 'Failed to update profile. Please try again.')
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t('please_login', 'Please log in to view your info')}</p>
      </div>
    );
  }

  // Create full name by combining first and last name
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();

  const handleOpenDeleteModal = () => {
    setDeleteModalVisible(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalVisible(false);
  };

  const handleOpenConfirmationModal = () => {
    setDeleteModalVisible(false);
    setConfirmationModalVisible(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmationModalVisible(false);
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteUser();
      setConfirmationModalVisible(false);
      setFinalModalVisible(true);

      setTimeout(() => {
        dispatch(logout(false));
        window.location.href = '/';
      }, 3000);
    } catch (e) {
      console.error('Error deleting account:', e);
    }
  };

  const handleCloseFinalModal = () => {
    dispatch(logout(false));
    window.location.href = '/';
  };

  const infoItems = [
    {
      icon: User,
      label: t('profile_name', 'Name'),
      value: fullName || t('n_a', 'N/A'),
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      icon: AtSign,
      label: t('profile_username', 'Username'),
      value: user.username || t('n_a', 'N/A'),
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      icon: Mail,
      label: t('profile_email', 'Email'),
      value: user.email,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
    },
  ];

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs sm:text-sm font-medium text-gray-500">
          {t('profile_details', 'Profile details')}
        </p>
        {!isEditing && (
          <button
            onClick={startEditing}
            className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            {t('edit_profile_action', 'Edit profile')}
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3 sm:space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="profile-firstName" className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide font-medium">
              {t('firstName', 'First name')}
            </label>
            <input
              id="profile-firstName"
              type="text"
              value={form.firstName}
              onChange={handleFieldChange('firstName')}
              disabled={isSaving}
              className="w-full px-3 py-2.5 text-sm sm:text-base rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60"
              placeholder={t('firstName', 'First name')}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="profile-lastName" className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide font-medium">
              {t('lastName', 'Last name')}
            </label>
            <input
              id="profile-lastName"
              type="text"
              value={form.lastName}
              onChange={handleFieldChange('lastName')}
              disabled={isSaving}
              className="w-full px-3 py-2.5 text-sm sm:text-base rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60"
              placeholder={t('lastName', 'Last name')}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="profile-username" className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide font-medium">
              {t('profile_username', 'Username')}
            </label>
            <input
              id="profile-username"
              type="text"
              value={form.username}
              onChange={handleFieldChange('username')}
              disabled={isSaving}
              className="w-full px-3 py-2.5 text-sm sm:text-base rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60"
              placeholder={t('profile_username', 'Username')}
            />
          </div>

          <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">
                {t('profile_email', 'Email')}
              </p>
              <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                {user.email}
              </p>
            </div>
          </div>

          {formError && (
            <p role="alert" className="text-xs sm:text-sm text-red-600 font-medium">
              {formError}
            </p>
          )}

          <div className="flex items-center gap-2.5 sm:gap-3 pt-1">
            <button
              onClick={handleSaveProfile}
              disabled={isSaving}
              className="flex-1 min-h-[44px] flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm sm:text-base font-semibold rounded-xl transition-colors disabled:opacity-60"
            >
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSaving ? t('saving', 'Saving...') : t('save', 'Save')}
            </button>
            <button
              onClick={cancelEditing}
              disabled={isSaving}
              className="flex-1 min-h-[44px] bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm sm:text-base font-semibold rounded-xl transition-colors disabled:opacity-60"
            >
              {t('cancel', 'Cancel')}
            </button>
          </div>
        </div>
      ) : (
        /* User Info Cards */
        <div className="space-y-2.5 sm:space-y-3">
          {infoItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-xl"
              >
                <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-full ${item.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${item.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">
                    {item.label}
                  </p>
                  <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                    {item.value}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Account Section */}
      <div className="pt-3 mt-3 sm:pt-4 sm:mt-4 border-t border-gray-200">
        <div className="flex items-start gap-2.5 sm:gap-3 p-3 sm:p-4 bg-red-50 rounded-xl">
          <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs sm:text-sm font-medium text-red-800 mb-1">
              {t('danger_zone', 'Danger Zone')}
            </p>
            <p className="text-xs sm:text-sm text-red-700">
              <button
                onClick={handleOpenDeleteModal}
                className="font-semibold underline hover:no-underline transition-all"
              >
                {t('click_here', 'Click here')}
              </button>
              {' '}{t('delete_account_text', 'to delete your account')}
            </p>
          </div>
        </div>
      </div>

      {/* Modals */}
      {isDeleteModalVisible && (
        <DeleteAccountModal
          onClose={handleCloseDeleteModal}
          onConfirm={handleOpenConfirmationModal}
        />
      )}

      {isConfirmationModalVisible && (
        <ConfirmationModal
          onConfirm={handleDeleteAccount}
          onCancel={handleCloseConfirmationModal}
          type="delete-account"
        />
      )}

      {isFinalModalVisible && <FinalConfirmationModal onClose={handleCloseFinalModal} />}
    </div>
  );
};

export default ProfileInfo;
